import React, {FC, useEffect, useRef, useState} from 'react';
import SortFilter, {IFilterValues} from './SortFilter';
import useDebounce from '../../../hooks/useDebounce';

interface ISortFilterWithTextBufferProps {
  filterValues: IFilterValues;
  onFilterChange: (filters: IFilterValues) => void;
}

/**
 * Wrapper component that adds text input buffering and debounced propagation
 * to prevent keystroke loss and excessive URL / re-render overhead.
 *
 * Text inputs (email, username) are buffered locally for instant feedback.
 * Their values are debounced (300ms) before propagating to the parent, which
 * prevents expensive setSearchParams calls on every keystroke.
 *
 * Non-text changes (dropdowns, autocomplete, dates, sort) propagate immediately.
 *
 * @see {SortFilter} for the presentational component
 */
const SortFilterWithTextBuffer: FC<ISortFilterWithTextBufferProps> = (props) => {
  const {filterValues, onFilterChange} = props;

  /**
   * Buffer state for email and username inputs to prevent keystroke loss
   * when filterValues are updated from outside (e.g., URL navigation).
   */
  const [bufferValues, setBufferValues] = useState({
    email: filterValues.email,
    username: filterValues.username,
  });

  // Sync buffer state when external filter values change (e.g., URL navigation, reset)
  useEffect(() => {
    setBufferValues(prev => {
      if (prev.email === filterValues.email && prev.username === filterValues.username) return prev;
      return { email: filterValues.email, username: filterValues.username };
    });
  }, [filterValues.email, filterValues.username]);

  // Debounce text values before propagating to URL
  const debouncedBuffer = useDebounce(bufferValues, 300);

  // Refs to avoid stale closures in debounced effect
  const onFilterChangeRef = useRef(onFilterChange);
  const filterValuesRef = useRef(filterValues);
  onFilterChangeRef.current = onFilterChange;
  filterValuesRef.current = filterValues;

  // Propagate debounced text changes to parent (URL update)
  useEffect(() => {
    const current = filterValuesRef.current;
    if (debouncedBuffer.email !== current.email || debouncedBuffer.username !== current.username) {
      onFilterChangeRef.current({
        ...current,
        email: debouncedBuffer.email,
        username: debouncedBuffer.username,
      });
    }
  }, [debouncedBuffer]);

  // Create buffered filter values that combine URL state with local buffer state
  const bufferedFilterValues: IFilterValues = {
    ...filterValues,
    email: bufferValues.email,
    username: bufferValues.username,
  };

  // Intercept filter changes to update buffer state for text inputs
  const handleFilterChange = (filters: IFilterValues) => {
    // Always update text buffer for instant visual feedback
    setBufferValues({ email: filters.email, username: filters.username });

    // Check if only text fields changed
    const isOnlyTextChange =
      filters.type === filterValues.type &&
      filters.companyName === filterValues.companyName &&
      filters.accountId === filterValues.accountId &&
      filters.activated === filterValues.activated &&
      filters.creationDate === filterValues.creationDate &&
      filters.sortBy === filterValues.sortBy &&
      filters.sortDir === filterValues.sortDir;

    if (!isOnlyTextChange) {
      // Non-text changes propagate immediately (with current buffer text values)
      onFilterChange(filters);
    }
    // Text-only changes are debounced via the useEffect above
  };

  return (
    <SortFilter
      filterValues={bufferedFilterValues}
      onFilterChange={handleFilterChange}
    />
  );
};

export default SortFilterWithTextBuffer;
